import api from '../config/api';

/**
 * Drivers Service
 * Handles driver related API calls
 */

/**
 * Fetch drivers list with pagination
 * @param {Object} params
 * @param {number} params.page - page number (1-based)
 * @param {number} params.limit - items per page
 * @param {string} params.search - search term
 * @param {string} params.status - driver status filter
 * @param {number|string} params.agency_id - filter by agency ID
 * @returns {Promise<Object>} response data
 */
export const getDrivers = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = 10,
      search,
      status,
      agency_id,
    } = params;

    const offset = (page - 1) * limit;
    const queryParams = {
      limit,
      offset,
    };

    if (search) {
      queryParams.search = search;
    }

    if (status) {
      queryParams.status = status;
    }

    if (agency_id) {
      queryParams.agency_id = agency_id;
    }

    const response = await api.get('/api/drivers', { params: queryParams });
    return response.data;
  } catch (error) {
    // Preserve the original error object with all details for better debugging
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch drivers. Please try again.';
    
    // Create a new error that preserves the original error details
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    enhancedError.response = error.response;
    enhancedError.status = error.response?.status;
    enhancedError.data = error.response?.data;
    
    throw enhancedError;
  }
};

/**
 * Create a new driver
 * @param {Object} driverData - Driver data
 * @param {File|null} driverData.photo - Photo file (optional)
 * @returns {Promise} API response with created driver data
 */
export const createDriver = async (driverData) => {
  try {
    // Check if photo is a File object - if so, use FormData
    const hasPhotoFile = driverData.photo && driverData.photo instanceof File;
    
    let payload;
    let config = {};
    
    if (hasPhotoFile) {
      // Use FormData for file upload
      payload = new FormData();
      payload.append('full_name', driverData.full_name || '');
      payload.append('mobile_number', driverData.mobile_number || '');
      payload.append('alternate_number', driverData.alternate_number || '');
      payload.append('date_of_birth', driverData.date_of_birth || '');
      payload.append('address', driverData.address || '');
      payload.append('license_number', driverData.license_number || '');
      payload.append('license_type', driverData.license_type || '');
      payload.append('aadhar_number', driverData.aadhar_number || '');
      payload.append('pan_number', driverData.pan_number || '');
      payload.append('joining_date', driverData.joining_date || '');
      if (driverData.agency_id) {
        payload.append('agency_id', driverData.agency_id);
      }
      if (driverData.assigned_vehicle_id) {
        payload.append('assigned_vehicle_id', driverData.assigned_vehicle_id);
      }
      if (driverData.medical_certificate_number) {
        payload.append('medical_certificate_number', driverData.medical_certificate_number);
      }
      payload.append('driver_status', driverData.driver_status || 'Active');
      payload.append('photo', driverData.photo); // Send file in "photo" key
      
      // Content-Type will be automatically set by axios interceptor for FormData
    } else {
      // Use JSON for regular data
      payload = { ...driverData };
      // include assigned vehicle id if provided
      if (payload.assigned_vehicle_id === undefined && driverData.assigned_vehicle_id) {
        payload.assigned_vehicle_id = driverData.assigned_vehicle_id;
      }
      // Remove photo if it's not a file
      if (payload.photo && !(payload.photo instanceof File)) {
        delete payload.photo;
      }
    }
    
    const response = await api.post('/api/drivers', payload, config);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to create driver. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update a driver
 * @param {number|string} id - Driver ID
 * @param {Object} driverData - Updated driver data
 * @param {File|null} driverData.photo - Photo file (optional)
 * @returns {Promise} API response with updated driver data
 */
export const updateDriver = async (id, driverData) => {
  try {
    // Check if photo is a File object - if so, use FormData
    const hasPhotoFile = driverData.photo && driverData.photo instanceof File;
    
    let payload;
    let config = {};
    
    if (hasPhotoFile) {
      // Use FormData for file upload
      payload = new FormData();
      payload.append('full_name', driverData.full_name || '');
      payload.append('mobile_number', driverData.mobile_number || '');
      payload.append('alternate_number', driverData.alternate_number || '');
      payload.append('date_of_birth', driverData.date_of_birth || '');
      payload.append('address', driverData.address || '');
      payload.append('license_number', driverData.license_number || '');
      payload.append('license_type', driverData.license_type || '');
      payload.append('aadhar_number', driverData.aadhar_number || '');
      payload.append('pan_number', driverData.pan_number || '');
      payload.append('joining_date', driverData.joining_date || '');
      if (driverData.agency_id) {
        payload.append('agency_id', driverData.agency_id);
      }
      if (driverData.assigned_vehicle_id) {
        payload.append('assigned_vehicle_id', driverData.assigned_vehicle_id);
      }
      if (driverData.medical_certificate_number) {
        payload.append('medical_certificate_number', driverData.medical_certificate_number);
      }
      payload.append('driver_status', driverData.driver_status || 'Active');
      payload.append('photo', driverData.photo); // Send file in "photo" key
      
      // Content-Type will be automatically set by axios interceptor for FormData
    } else {
      // Use JSON for regular data
      payload = { ...driverData };
      // Remove photo if it's not a file (e.g., if it's a URL string)
      if (payload.photo && !(payload.photo instanceof File)) {
        delete payload.photo;
      }
    }
    
    const response = await api.put(`/api/drivers/${id}`, payload, config);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to update driver. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a driver
 * @param {number|string} id - Driver ID
 * @returns {Promise} API response
 */
export const deleteDriver = async (id) => {
  try {
    const response = await api.delete(`/api/drivers/${id}`);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to delete driver. Please try again.';
    throw new Error(errorMessage);
  }
};

